import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactHistoryTab = ({ contact, messageHistory = [] }) => {
  const [filter, setFilter] = useState('all');

  const mockHistory = [
    {
      id: 1,
      type: 'birthday',
      message: `Happy Birthday ${contact?.name}! 🎉 Hope you have a wonderful day filled with joy and celebration!`,
      timestamp: new Date('2024-03-15T09:00:00'),
      status: 'delivered',
      method: 'email'
    },
    {
      id: 2,
      type: 'random',
      message: `Hi ${contact?.name}! Just wanted to check in and see how you're doing. Hope everything is going well!`,
      timestamp: new Date('2024-02-28T14:30:00'),status: 'delivered',method: 'sms'
    },
    {
      id: 3,
      type: 'manual',
      message: `Thanks for the great meeting today! Looking forward to our next collaboration.`,
      timestamp: new Date('2024-02-20T16:45:00'),status: 'read',method: 'email'
    },
    {
      id: 4,
      type: 'birthday',message: `Happy Birthday! 🎂 Wishing you all the best on your special day!`,timestamp: new Date('2023-03-15T08:00:00'),status: 'delivered',method: 'whatsapp'
    },
    {
      id: 5,
      type: 'random',message: `Hey! Hope you're having a great week. Let's catch up soon!`,timestamp: new Date('2024-01-10T11:20:00'),status: 'failed',method: 'sms'
    }
  ];

  const allHistory = messageHistory?.length > 0 ? messageHistory : mockHistory;

  const filteredHistory = filter === 'all' 
    ? allHistory 
    : allHistory?.filter(item => item?.type === filter);

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case 'birthday': return 'Gift';
      case 'random': return 'Shuffle';
      case 'manual': return 'Edit';
      default: return 'MessageSquare';
    }
  };

  const getMessageTypeLabel = (type) => {
    switch (type) {
      case 'birthday': return 'Birthday Wish';
      case 'random': return 'Random Contact';
      case 'manual': return 'Manual Message';
      default: return 'Message';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return 'Check';
      case 'read': return 'CheckCheck';
      case 'failed': return 'X';
      case 'pending': return 'Clock';
      default: return 'Clock';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'text-success';
      case 'read': return 'text-primary';
      case 'failed': return 'text-error';
      case 'pending': return 'text-warning';
      default: return 'text-text-secondary';
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'email': return 'Mail';
      case 'sms': return 'MessageSquare';
      case 'whatsapp': return 'MessageCircle';
      default: return 'Send';
    }
  };

  return (
    <div className="p-6">
      {/* Filter Tabs */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-text-primary">Message History</h3>
        
        <div className="flex space-x-1 bg-muted rounded-lg p-1">
          {[
            { id: 'all', label: 'All' },
            { id: 'birthday', label: 'Birthday' },
            { id: 'random', label: 'Random' },
            { id: 'manual', label: 'Manual' }
          ]?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setFilter(tab?.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-micro ${
                filter === tab?.id
                  ? 'bg-surface text-text-primary shadow-card'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {tab?.label}
            </button>
          ))}
        </div>
      </div>
      {/* History Timeline */}
      <div className="space-y-4">
        {filteredHistory?.length > 0 ? (
          filteredHistory?.map((item, index) => (
            <div key={item?.id} className="relative">
              {/* Timeline Line */}
              {index < filteredHistory?.length - 1 && (
                <div className="absolute left-6 top-12 w-0.5 h-16 bg-border"></div>
              )}
              
              <div className="flex space-x-4 p-4 bg-surface border border-border rounded-lg hover:shadow-card transition-micro">
                {/* Timeline Dot */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  item?.type === 'birthday' ? 'bg-accent text-accent-foreground' :
                  item?.type === 'random' ? 'bg-primary text-primary-foreground' :
                  'bg-secondary text-secondary-foreground'
                }`}>
                  <Icon name={getMessageTypeIcon(item?.type)} size={20} />
                </div>

                {/* Message Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-text-primary">
                          {getMessageTypeLabel(item?.type)}
                        </span>
                        <div className="flex items-center space-x-1">
                          <Icon name={getMethodIcon(item?.method)} size={14} className="text-text-secondary" />
                          <span className="text-xs text-text-secondary capitalize">
                            {item?.method}
                          </span>
                        </div>
                      </div>
                      
                      <div className="text-sm text-text-secondary">
                        {item?.timestamp?.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>

                    {/* Status */}
                    <div className={`flex items-center space-x-1 ${getStatusColor(item?.status)}`}>
                      <Icon name={getStatusIcon(item?.status)} size={16} />
                      <span className="text-xs capitalize">{item?.status}</span>
                    </div>
                  </div>

                  {/* Message Text */}
                  <div className="bg-muted/50 rounded-lg p-3 mb-3">
                    <p className="text-text-primary text-sm leading-relaxed">
                      {item?.message}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Reply"
                      iconPosition="left"
                    >
                      Reply
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Copy"
                      iconPosition="left"
                    >
                      Copy
                    </Button>
                    
                    {item?.status === 'failed' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="RefreshCw"
                        iconPosition="left"
                        className="text-warning hover:text-warning"
                      >
                        Retry
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="MessageSquare" size={24} className="text-text-secondary" />
            </div>
            <h4 className="text-lg font-medium text-text-primary mb-2">No Messages Yet</h4>
            <p className="text-text-secondary mb-4">
              Start a conversation with {contact?.name} to see message history here.
            </p>
            <Button
              variant="default"
              iconName="Send"
              iconPosition="left"
            >
              Send First Message
            </Button>
          </div>
        )}
      </div>
      {/* Statistics */}
      {filteredHistory?.length > 0 && (
        <div className="mt-8 pt-6 border-t border-border">
          <h4 className="text-md font-semibold text-text-primary mb-4">Communication Stats</h4>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-text-primary">
                {allHistory?.length}
              </div>
              <div className="text-sm text-text-secondary">Total Messages</div>
            </div>
            
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-success">
                {allHistory?.filter(h => h?.status === 'delivered' || h?.status === 'read')?.length}
              </div>
              <div className="text-sm text-text-secondary">Delivered</div>
            </div>
            
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-accent">
                {allHistory?.filter(h => h?.type === 'birthday')?.length}
              </div>
              <div className="text-sm text-text-secondary">Birthday Wishes</div>
            </div>
            
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">
                {allHistory?.filter(h => h?.type === 'random')?.length}
              </div>
              <div className="text-sm text-text-secondary">Random Contacts</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactHistoryTab;